/**
 * FAQ section for a service detail page. Renders a bespoke accordion — distinct from the
 * shared FAQ/FaqAccordion the /faq pages use — matching the service-page Figma: a bordered
 * "View All FAQs" button and chevron rows with single-open behavior (the first FAQ expanded
 * by default). FAQs are fetched server-side via getFaqsByTag(slug) and passed in; the section
 * hides itself when there are none.
 */
/* eslint-disable @next/next/no-img-element */
import React, { useState } from 'react'
import Link from 'next/link'
import styled from '@emotion/styled'
import { gsColors as c } from '../../styles/Variables'
import { mq } from '../../helpers'
import { faqAnswerToHtml } from '../../helpers/faq'

/**
 * @param {{ faqs?: Array<object> }} props
 */
const FaqSection = ({ faqs = [] }) => {
  const [openId, setOpenId] = useState(faqs[0]?.id ?? null)

  if (!faqs.length) {
    return null
  }

  return (
    <Wrapper>
      <div className="head">
        <h2>FAQs</h2>
        <p>Got questions? We have answers.</p>
        <Link href="/faq" legacyBehavior><a className="all">View All FAQs</a></Link>
      </div>

      <div className="list">
        {faqs.map(faq => {
          const question = faq.question || faq.title
          if (!question) {
            return null
          }
          const isOpen = openId === faq.id
          const panelId = `faq-panel-${faq.id}`

          return (
            <div key={faq.id} className="item">
              <button
                type="button"
                className="q"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenId(isOpen ? null : faq.id)}
              >
                <span>{question}</span>
                <img
                  className={isOpen ? 'chevron open' : 'chevron'}
                  src="/images/nav-arrow-down.svg"
                  alt=""
                  aria-hidden="true"
                  width={24}
                  height={18}
                />
              </button>
              {isOpen && (
                <div
                  id={panelId}
                  className="a"
                  dangerouslySetInnerHTML={{ __html: faqAnswerToHtml(faq.answer ?? faq.body) }}
                />
              )}
            </div>
          )
        })}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 48px;
  padding: 64px 88px;
  ${mq('max', '900px')} {
    grid-template-columns: 1fr;
    gap: 24px;
    padding: 40px 24px;
  }

  h2 {
    font-family: Garnett;
    font-size: 32px;
    font-weight: 750;
    color: ${c.black};
    margin: 0 0 12px;
    ${mq('max', '900px')} {
      font-size: 28px;
    }
  }
  .head p {
    font-family: Euclid;
    font-size: 16px;
    color: ${c.darkGrey};
    margin: 0 0 24px;
  }
  .all {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 16px 32px;
    border: 1px solid ${c.black};
    font-family: Euclid;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.96px;
    color: ${c.black};
    text-decoration: none;
    transition: background-color 0.2s ease, color 0.2s ease;

    &:hover {
      background: ${c.black};
      color: ${c.white};
    }
  }

  .list {
    display: flex;
    flex-direction: column;
  }

  .item {
    border-bottom: 1px solid ${c.sand};
  }

  .q {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    background: none;
    border: none;
    padding: 24px 0;
    cursor: pointer;
    font-family: Euclid;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.4;
    color: ${c.black};
    text-align: left;
    ${mq('max', '900px')} {
      font-size: 16px;
      padding: 20px 0;
    }
  }

  .chevron {
    flex-shrink: 0;
    transition: transform 0.2s ease;
    transform: rotate(0deg);
  }
  .chevron.open {
    transform: rotate(180deg);
  }

  .a {
    font-family: Euclid;
    font-size: 16px;
    line-height: 1.6;
    color: ${c.darkGrey};
    padding: 0 48px 24px 0;
    ${mq('max', '900px')} {
      font-size: 15px;
      padding-right: 0;
    }

    p {
      margin: 0 0 16px;
      &:last-child {
        margin-bottom: 0;
      }
    }

    a {
      color: ${c.cobalt};
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }
`

export default FaqSection
